/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import gql from 'graphql-tag'
import moment from 'moment'

import {
  Spinner,
  Card,
  SvgIcon,
  Grid,
  SpanWithData,
  LabelledDetail as Detail,
  NavLink,
  Warning
} from '../'

import {
  showAsCurrency
} from '../../utils'

class PaymentDetails extends React.Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    className: PropTypes.string
  }

  constructor(props) {
    super(props)

    this.state = {
      refetch: false
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.data.error && !this.state.refetch) {
      this.setState({
        refetch: true
      })
      nextProps.data.refetch()
    }

    if (this.state.refetch) {
      this.setState({
        refetch: false
      })
    }
  }

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.data.error) {
      return (
        <Warning>
          Loading account data failed.
        </Warning>
      )
    }

    let { payer, payee, identityFor, createdAt, status, state, source, originalAmount, reference,
      workflowrunDeducting, workflowrunReceipting, paymentType, prod, amount, method } = this.props.data.payment

    let details = [
      <Detail
        label='Payer'
        detail={
          <NavLink to={`/os/identity/${payer.id}`}>
            {payer.fullName}
          </NavLink>
        }
        detailFontWeight='bold'
      />,
      <Detail
        label='Identity for'
        detail={
          identityFor
            ? <NavLink to={`/os/identity/${identityFor.id}`}>
                {identityFor.fullName}
              </NavLink>
            : '-'
        }
        detailFontWeight='bold'
      />,
      <Detail
        label='Payee'
        detail={
          <NavLink to={`/os/identity/${payee.id}`}>
            {payee.fullName}
          </NavLink>
        }
        detailFontWeight='bold'
      />,
      <Detail
        label='Created at'
        detail={moment(createdAt).format('MMMM DD')}
        detailFontWeight='bold'
      />,
      <Detail
        label='Status'
        detail={<SpanWithData forField='PaymentStatus' searchKey={status} />}
        detailFontWeight='bold'
      />,
      <Detail
        label='State'
        detail={<SpanWithData forField='PaymentState' searchKey={state} />}
        detailFontWeight='bold'
      />,
      <Detail
        label='Source'
        detail={<SpanWithData forField='PaymentSource' searchKey={source} />}
        detailFontWeight='bold'
      />,
      <Detail
        label='Original amount'
        detail={showAsCurrency('R', originalAmount)}
        detailFontWeight='bold'
      />,
      <Detail
        label='Reference'
        detail={reference}
        detailFontWeight='bold'
      />,
      <Detail
        label='Deducting workflow'
        detail={
          workflowrunDeducting
          ? <NavLink to={`/os/operations/general/workflows/${workflowrunDeducting.id}`}>
              {workflowrunDeducting.description}
            </NavLink>
          : 'Unknown'
        }
        detailFontWeight='bold'
      />,
      <Detail
        label='Receipting workflow'
        detail={
          workflowrunReceipting
          ? <NavLink to={`/os/operations/general/workflows/${workflowrunReceipting.id}`}>
              {workflowrunReceipting.description}
            </NavLink>
          : 'Unknown'
        }
        detailFontWeight='bold'
      />
    ]

    return (
      <div className={this.props.className ? this.props.className : ''}>
        <Card
          title='Payment'
          icon={
            <SvgIcon
              variant='utility'
              symbol='moneybag'
            />
          }
          body={
            <div>
              <Grid
                items={[
                  <Detail
                    label='Type'
                    detail={<SpanWithData forField='PaymentPaymentType' searchKey={paymentType} />}
                    detailFontSize='large'
                    detailFontWeight='bold'
                    className='slds-p-bottom_small'
                  />,
                  <Detail
                    label='Product'
                    detail={prod
                      ? prod.name
                      : '-'
                    }
                    detailFontWeight='bold'
                    detailFontSize='large'
                  />,
                  <Detail
                    label='Amount'
                    detail={showAsCurrency('R', amount)}
                    detailFontWeight='bold'
                    detailFontSize='large'
                  />,
                  <Detail
                    label='Method'
                    detail={<SpanWithData forField='PaymentMethod' searchKey={method} />}
                    detailFontWeight='bold'
                    detailFontSize='large'
                  />,
                ]}
              />
              <Grid
                items={details}
                className='slds-m-top_medium slds-m-bottom_medium'
                itemClassName='slds-m-top_small slds-m-bottom_small slds-p-bottom_small slds-border_bottom slds-p-right_small'
              />
            </div>
          }
          paddingTRL={true}
        />
      </div>
    )
  }
}

const getPayment = gql`
  query($id: ID!) {
    payment(id: $id) {
      id
      paymentType
      amount
      originalAmount
      source
      method
      reference
      state
      status
      createdAt
      prod {
        id
        name
      }
      identityFor {
        id
        fullName
      }
      payer {
        id
        fullName
      }
      payee {
        id
        fullName
      }
      workflowrunDeducting {
        id
        description
      }
      workflowrunReceipting {
        id
        description
      }
    }
  }
`

const withData =
  graphql(getPayment, {
    options: ({ payment }) => ({
      variables: {
        id: payment
      }
    })
  })

export default withData(PaymentDetails)



// WEBPACK FOOTER //
// ./src/components/payment-details/index.js